import { useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { List, X } from "@phosphor-icons/react";
import { useLang } from "../i18n/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useLang();

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/about", label: t("nav.about") },
    { to: "/range", label: t("nav.products") },
    { to: "/quality", label: t("nav.quality") },
    { to: "/contact", label: t("nav.contact") },
  ];

  return (
    <header
      data-testid="site-navbar"
      className="fixed top-0 left-0 right-0 z-50 bg-[#1E3A5F] border-b border-[#15273F] shadow-md"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="logo-link"
          className="flex items-center gap-3 group"
          onClick={() => setOpen(false)}
        >
          <span className="relative inline-flex items-center justify-center w-12 h-12 overflow-hidden">
            <img
              src="/brand/omc-logo-512.png"
              alt="OMC LTD"
              className="w-12 h-12 object-contain"
            />
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-serif-display font-semibold text-2xl tracking-tight text-[#D4AF37] group-hover:text-[#E8C75A] transition-colors">
              OMC LTD
            </span>
            <span className="font-mono text-[10px] font-medium tracking-[0.25em] uppercase text-[#B8860B]">
              {t("common.brand")}
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace("/", "") || "home"}`}
              end={l.to === "/"}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${
                  isActive
                    ? "text-[#E8C75A] font-semibold border-b border-[#D4AF37] pb-1"
                    : "text-[#D4AF37] hover:text-[#E8C75A]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitcher />
          <button
            data-testid="navbar-quote-cta"
            onClick={() => navigate("/quote")}
            className="group relative inline-flex items-center gap-2 px-6 py-3 bg-[#6B1F2C] text-[#D4AF37] font-semibold tracking-wide hover:bg-[#4A1620] transition-colors"
          >
            {t("nav.quote")}
            <span className="w-4 h-px bg-[#D4AF37] group-hover:w-6 transition-all" />
          </button>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <LanguageSwitcher />
          <button
            data-testid="mobile-menu-toggle"
            className="text-[#D4AF37]"
            onClick={() => setOpen(!open)}
            aria-label="Toggle navigation"
          >
            {open ? <X size={26} /> : <List size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#15273F] bg-[#1E3A5F]">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `text-left py-2 transition-colors ${
                    isActive ? "text-[#E8C75A] font-semibold" : "text-[#D4AF37] hover:text-[#E8C75A]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/quote"
              data-testid="mobile-quote-cta"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center px-6 py-3 bg-[#6B1F2C] text-[#D4AF37] font-semibold"
            >
              {t("nav.quote")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
